/* « Organisation interne (facultatif) » — the étiquettes picker, built once (ruling « Organisation », 19/10): on the
   builder's step 3 (a draft), in a published campaign's Paramètres tab, and in the support designer. Toggle the
   workspace's étiquettes on this item (5 at most: the 6th is refused with one line), or create one inline and pick it
   (only for who writes the list — owner, admin, a manager over every establishment). Row data: a change is written to
   the item's étiquettes at once (lib/tags › setItemTags; lib-pack-21: PATCH { tags }) and never touches a campaign's
   content — no « Modifications à publier », no version. Before the item exists (a new draft, a new support) the choice
   is held here (or by the parent, `value`/`onChange`) and written when the id arrives. Never shown to a customer.
   Also: the chips a list row shows (EtiquettesChips) and the lists' « Étiquette » select (EtiquetteFilter). */
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Input, Pill, Select } from '@/ui'
import { useLocations } from '@/lib/api'
import {
  addTag, canWriteTagList, itemTags, setItemTags, useTagStore, useTags,
  MAX_PER_ITEM, TAG_PER_ITEM_LINE, type Tag, type TagKind,
} from '@/lib/tags'
import { editRefusalLines, settled, writeMark } from '@/lib/writes'

export const ORGANISATION_TITLE = 'Organisation interne (facultatif)'
export const ORGANISATION_LEAD = 'Des repères internes, sans effet sur le parcours client.'

export function EtiquettesPicker({ kind, id, canEdit, value, onChange }: {
  kind: TagKind
  /** the item's id; null while it does not exist yet (a new draft, a new support) */
  id: string | null
  /** the item's own write rights (canBuild for a campaign or a support) */
  canEdit: boolean
  /** with `id` null: the parent holds the choice (the designer writes it once the support is created) */
  value?: string[]; onChange?: (ids: string[]) => void
}) {
  const tags = useTags()
  useTagStore()
  const { data: locations } = useLocations()
  const listWriter = canWriteTagList((locations ?? []).map(l => l.id))
  const [held, setHeld] = useState<string[]>([])
  const [refusal, setRefusal] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  /* the choice before the item exists is written once its id arrives; a renamed id (the server's name for a draft)
     takes the étiquettes of the first one */
  const prev = useRef<string | null>(id)
  useEffect(() => {
    const was = prev.current; prev.current = id
    if (!id) return
    if (!was && held.length && !onChange) { setItemTags(kind, id, held); setHeld([]) }
    else if (was && was !== id && itemTags(kind, id).length === 0) { const moved = itemTags(kind, was).map(t => t.id); if (moved.length) setItemTags(kind, id, moved) }
  }, [id]) // eslint-disable-line react-hooks/exhaustive-deps
  const chosen: string[] = id ? itemTags(kind, id).map(t => t.id) : (value ?? held)
  /** with an API, a refused write is said here once the server answers (the item's étiquettes are read back) */
  const hear = (mark: number) => { void settled(mark).then(no => { if (no) setRefusal(editRefusalLines(no)[0]) }) }
  const put = (ids: string[], mark = writeMark()): boolean => {
    if (ids.length > MAX_PER_ITEM) { setRefusal(TAG_PER_ITEM_LINE); return false }
    setRefusal(null)
    if (id) { const r = setItemTags(kind, id, ids); if (r) { setRefusal(r); return false } hear(mark) }
    else if (onChange) onChange(ids)
    else setHeld(ids)
    return true
  }
  const toggle = (t: Tag) => { put(chosen.includes(t.id) ? chosen.filter(x => x !== t.id) : [...chosen, t.id]) }
  const create = () => {
    if (chosen.length >= MAX_PER_ITEM) { setRefusal(TAG_PER_ITEM_LINE); return }
    const mark = writeMark()
    const r = addTag(draft)
    if (typeof r === 'string') { setRefusal(r); return }
    setDraft('')
    /* without an item yet, the new étiquette's own write is heard here; with one, `put` hears both */
    if (!put([...chosen, r.id], mark) || !id) hear(mark)
  }
  const shown = canEdit ? tags : tags.filter(t => chosen.includes(t.id))
  return (
    <div data-testid="etiquettes-picker" style={{ display: 'grid', gap: 8 }}>
      <div>
        <p className="t-eyebrow" style={{ margin: 0 }}>{ORGANISATION_TITLE}</p>
        <p className="f-help" style={{ margin: '2px 0 0' }}>{ORGANISATION_LEAD}</p>
      </div>
      {shown.length === 0
        ? <p className="f-help" style={{ margin: 0 }} data-testid="etiquettes-vide">{canEdit || tags.length === 0 ? 'Aucun terme créé.' : 'Aucune étiquette.'}</p>
        : <div className="chips" role="group" aria-label="Étiquettes">
          {shown.map(t => <button key={t.id} type="button" className={'chip' + (chosen.includes(t.id) ? ' on' : '')} aria-pressed={chosen.includes(t.id)}
            disabled={!canEdit} data-testid="etiquette-choix" data-tag={t.name} onClick={() => toggle(t)}>{t.name}</button>)}
        </div>}
      {canEdit && listWriter && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <Input aria-label="Nouvelle étiquette" placeholder="Nouvelle étiquette" value={draft} maxLength={40} style={{ flex: '1 1 160px', minWidth: 0, maxWidth: 260 }}
            onChange={e => { setDraft(e.target.value); setRefusal(null) }} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); create() } }} />
          <Button size="sm" onClick={create} disabled={!draft.trim()}>Ajouter</Button>
          <Link to="/parametres/organisation" className="f-help">Gérer les étiquettes</Link>
        </div>
      )}
      {refusal && <p className="f-help err" role="alert" style={{ margin: 0 }} data-testid="etiquettes-refus">{refusal}</p>}
    </div>
  )
}

/** The chips a row shows (5 at most, the item's own étiquettes, never a customer's sight). */
export function EtiquettesChips({ kind, id }: { kind: TagKind; id: string }) {
  useTagStore()
  const list = itemTags(kind, id).slice(0, MAX_PER_ITEM)
  if (!list.length) return null
  return <span data-testid="etiquettes" style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>{list.map(t => <Pill key={t.id} tone="ink">{t.name}</Pill>)}</span>
}

/** The lists' « Étiquette » select: '' = « Toutes les étiquettes ». Drawn only when the workspace has étiquettes. */
export function EtiquetteFilter({ value, onChange, field = true }: { value: string; onChange: (id: string) => void; field?: boolean }) {
  const tags = useTags()
  if (!tags.length) return null
  const select = <Select aria-label="Étiquette" value={tags.some(t => t.id === value) ? value : ''} onChange={e => onChange(e.target.value)}>
    <option value="">Toutes les étiquettes</option>
    {tags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
  </Select>
  return field ? <label className="field"><span className="f-label">Étiquette</span>{select}</label> : select
}
